import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { map, switchMap, take, takeWhile } from 'rxjs/operators';

import { ModelBase } from 'store/model-base';
import { ApiChannel } from 'core/constants';
import { buildHeaders, XmStore } from 'core/services';
import { RequestStatus } from './models';
import { StoreAction } from 'store/actions';
import { REQUEST_INTERVAL, RETRY_REQUEST_COUNT } from 'my-account/constants';

@Injectable()
export class RequestStatusApi {
    public static getRequestStatus(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        const apiUrl: string = params.isXOMChannel ? `/xmoms/requests/${params.requestId}` : `/requests/${params.requestId}`;

        return http.get(apiUrl, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static pollingRequestStatus(xmStore: XmStore, _http: HttpClient, params: RequestParams): Observable<RequestStatus> {
        return timer(REQUEST_INTERVAL, REQUEST_INTERVAL)
            .pipe(
                take(RETRY_REQUEST_COUNT),
                switchMap(() => xmStore.queryPromise<RequestStatus>(StoreAction.GET_REQUEST_STATUS, { requestId: params.requestId }))
            )
            .pipe(
                takeWhile((requestStatus: RequestStatus) => params.callback(requestStatus))
            );
    }

    public static switchPlan(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/lines/${params.lineId}/plan`, { planId: params.planId }, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static switchPlanViaSSMProxy(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post('/xm/ot/post/switch', { lineIdKey: params.lineId, planId: params.planId }, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params: { ...(params?.trialFeature && { trialFeature: params.trialFeature }) }
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static switchPlan8ViaSSMProxy(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        const { is_downgrade, ...bodyParams }: ApiParams = params;

        return http.post('/xm/plan8/post/switch',  bodyParams, {
            headers: buildHeaders({ apiChannel: ApiChannel.SSM_GATEWAY }),
            params: { ...(is_downgrade ? { is_downgrade: true } : {}) }
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }

    public static switchAccountPlan(_xmStore: XmStore, http: HttpClient, params: ApiParams): Observable<RequestStatus> {
        return http.post(`/accounts/${params.accountId}/plan`, { bucketId: params.bucketId }, {
            headers: buildHeaders({ apiChannel: ApiChannel.GATEWAY })
        }).pipe(
            map((response: ApiResponse) => RequestStatus.create<RequestStatus>(response))
        );
    }
}

ModelBase.fetchMapping[StoreAction.POLL_REQUEST_STATUS] = RequestStatusApi.pollingRequestStatus;
ModelBase.fetchMapping[StoreAction.GET_REQUEST_STATUS] = RequestStatusApi.getRequestStatus;
ModelBase.persistMapping[StoreAction.SWITCH_PLAN] = RequestStatusApi.switchPlan;
ModelBase.persistMapping[StoreAction.SWITCH_PLAN_VIA_SSM_PROXY] = RequestStatusApi.switchPlanViaSSMProxy;
ModelBase.persistMapping[StoreAction.SWITCH_PLAN8_VIA_SSM_PROXY] = RequestStatusApi.switchPlan8ViaSSMProxy;
ModelBase.persistMapping[StoreAction.SWITCH_ACCOUNT_PLAN] = RequestStatusApi.switchAccountPlan;
